function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.promise.js";
import React from 'react';
import clsx from 'clsx';
import { ViewerControlButtonDownload, ViewerControlsBox, ViewerControlPagination, ViewerControlFlagDragScroll, ViewerControlButtonPositionRotate, ViewerControlZoomOverlay, ViewerControlButtonPrint } from "../../controls";
import { ViewerArchiveContainer, ViewerDocumentContainer, ViewerControlsContainer } from "../../container";
import { ViewArchiveInfoError, ViewArchiveInfoLoadingIcon } from "../../status";
import { PdfPasswordUnlock, usePdfPassword } from "./password";
import { ViewPdfPage } from "./page";
import { ViewPdfProgress } from "./progress";
import { isMobile } from '@isyc-react/core/device';
import { getParentPageElement, useDragPan, ZoomOption } from "../../base/page";
import { useArchiveLayoutDimensions } from "../../base/layout";
import { pageZoomScale } from "../../base/page";
import { Document, pdfjs } from 'react-pdf';
import '@isyc-react/design/main';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import "./view-pdf.style.scss";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
const onlyUnique = (value, index, self) => {
  return self.findIndex(v => {
    if (v && value) return v.originalHeight === value.originalHeight && v.originalWidth === value.originalWidth;
    return false;
  }) === index;
};
const cMapOptions = {
  // cMapUrl: 'cmaps/', // `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`
  cMapPacked: true,
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/'
};

/*
 * An object in which additional parameters to be passed to PDF.js can be defined.
 * For a full list of possible parameters, check PDF.js documentation on DocumentInitParameters.
 */

const initialPageIndex = 0;
const initialRotation = 0;
export const ViewPdf = props => {
  const {
    file,
    dataRange,
    annotations = [],
    onViewed,
    options
  } = props;
  // TODO default name ... with { t }
  const name = React.useMemo(() => {
    var _ref, _file$name;
    return (_ref = (_file$name = file === null || file === void 0 ? void 0 : file.name) !== null && _file$name !== void 0 ? _file$name : dataRange === null || dataRange === void 0 ? void 0 : dataRange.name) !== null && _ref !== void 0 ? _ref : 'document.pdf';
  }, [file, dataRange]);
  const url = React.useMemo(() => {
    var _dataRange$url;
    return (_dataRange$url = dataRange === null || dataRange === void 0 ? void 0 : dataRange.url) !== null && _dataRange$url !== void 0 ? _dataRange$url : null;
  }, [dataRange]);
  const initialZoom = options && options.defaultZoom ? options.defaultZoom : ZoomOption.Z_WINDOW_SIZE;
  const {
    layoutDim
  } = useArchiveLayoutDimensions();
  const [password, {
    onPassword,
    unlock
  }] = usePdfPassword();
  const [fileState, setFileState] = React.useState(file);
  const [loading, setLoading] = React.useState(null);
  const [scale, setScale] = React.useState(0);
  const [dragMove, setDragMove] = React.useState(true);
  const [rotation, setRotation] = React.useState(initialRotation);
  const [zoom, setZoom] = React.useState(initialZoom);
  const [pageIndex, setPageIndex] = React.useState(initialPageIndex);
  const [numPages, setNumPages] = React.useState(0);
  const [pageIndexDims, setPageIndexDims] = React.useState([]);
  const [pageDistinctDims, setPageDistinctDims] = React.useState([]);
  const pageRefs = React.useRef([]);
  const pdfRef = React.useRef(null);
  const readed = React.useRef([]);
  const pageIndexAnnotations = React.useMemo(() => {
    const result = Array.from(new Array(numPages), () => ({
      annotations: []
    }));
    const addAnnotation = (array, index, annotation) => {
      const currentIndex = index < 0 ? array.length + index : index;
      if (array[currentIndex]) array[currentIndex].annotations.push(annotation);
    };
    annotations.forEach(annotation => {
      if (annotation.pageIndex === 'all') result.forEach(r => r.annotations.push(annotation.render));else if (typeof annotation.pageIndex === 'number') addAnnotation(result, annotation.pageIndex, annotation.render);else annotation.pageIndex.forEach(i => addAnnotation(result, i, annotation.render));
    });
    return result;
  }, [numPages, annotations]);
  React.useEffect(() => {
    if (pageIndexDims.length) {
      const distinctDimensions = pageIndexDims.filter(onlyUnique);
      setPageDistinctDims(distinctDimensions);
    }
  }, [pageIndexDims]);
  React.useEffect(() => {
    if (typeof zoom === 'number') {
      setScale(zoom);
    } else {
      const autofitDims = pageDistinctDims.map(pageDim => pageZoomScale(zoom, pageDim, layoutDim, rotation));
      if (autofitDims.length) {
        const autofit = Math.min(...autofitDims);
        setScale(autofit);
      }
    }
  }, [zoom, pageDistinctDims, layoutDim, rotation]);
  const factor = React.useCallback(dim => dim / (1 / scale), [scale]);
  const onDocumentLoadSuccess = React.useCallback(pdf => {
    pdfRef.current = pdf;
    setNumPages(pdf.numPages);
    const promises = [...Array(pdf.numPages).keys()].map(pageIndex => pdf.getPage(pageIndex + 1));
    const pageIndexDimensions = [];
    const hasDataRangeTransport = Boolean(url);
    // console.log('HAS DATA RANGE TRANSPORT: ', hasDataRangeTransport)
    if (hasDataRangeTransport) {
      promises.forEach((promise, index) => {
        promise.then(page => {
          // console.log('GET PAGE: ', index + 1)
          const [left, top, width, height] = page.view;
          const originalWidth = width - left;
          const originalHeight = height - top;
          setPageIndexDims(indexDims => {
            const newIndexDims = [...indexDims];
            newIndexDims[index] = {
              originalHeight,
              originalWidth
            };
            return newIndexDims;
          });
        }).catch(e => {
          console.log('[PDF Viewer] Error getting pdf page: ', index + 1, e);
        });
      });
      // console.log('DOCUMENT LOADING SUCCESS CALLBACK: INIT GETPAGE DIMS', pdf)
    } else {
      Promise.all(promises).then(result => {
        result.forEach((page, i) => {
          // console.log('page view', i, page.view)
          const [left, top, width, height] = page.view;
          const originalWidth = width - left;
          const originalHeight = height - top;
          pageIndexDimensions[i] = {
            originalHeight,
            originalWidth
          };
        });
        setPageIndexDims(pageIndexDimensions);

        // const distinctDimensions = pageIndexDimensions.filter(onlyUnique)
        // setPageDistinctDims(distinctDimensions)

        // console.log('page index dims: ',pageIndexDimensions)
        // console.log('page distinct dims', distinctDimensions)
      }).catch(e => console.log('[PDF Viewer] Error getting some pdf page. ', e));
    }
  }, []);
  const dragPan = useDragPan(scale, dragMove);
  const goToPage = React.useCallback(pageIndex => {
    const pageDiv = pageRefs.current[pageIndex];
    if (pageDiv) {
      // TODO MAKE MORE ROBUST (view archive structure should have scroll always in the same site)
      const parent = getParentPageElement(pageDiv);
      if (parent) {
        parent.scrollTop = pageDiv.offsetTop - 8;
      }

      // this snippet is not working very well when there are inner scrolls (window and archive) and
      // is replaced by the upper one but maintained as deprecated just bc in the future it could be more robust
      // pageDiv.scrollIntoView()

      setPageIndex(pageIndex);
    }
  }, []);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ViewerArchiveContainer, {
    className: "view-pdf-container"
  }, password.incorrect ? /*#__PURE__*/React.createElement(ViewArchiveInfoError, null, /*#__PURE__*/React.createElement("h3", null, "Incorrect password for pdf file ", name)) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ViewerControlsContainer, {
    className: "view-pdf-tools"
  }, /*#__PURE__*/React.createElement(ViewerControlPagination, {
    numPages: numPages,
    currentPage: pageIndex,
    onChange: goToPage
  }), /*#__PURE__*/React.createElement(ViewerControlsBox, null, !isMobile && /*#__PURE__*/React.createElement(ViewerControlFlagDragScroll, {
    flag: dragMove,
    onChange: setDragMove
  }), /*#__PURE__*/React.createElement(ViewerControlZoomOverlay, {
    disabled: false,
    zoom: zoom,
    onChange: setZoom
  }), /*#__PURE__*/React.createElement(ViewerControlButtonPositionRotate, {
    disabled: false,
    rotation: rotation,
    onRotate: setRotation
  }), /*#__PURE__*/React.createElement(ViewerControlButtonPrint, {
    archive: {
      file,
      httpUrl: dataRange ? {
        url: dataRange.url
      } : undefined
    }
  }), /*#__PURE__*/React.createElement(ViewerControlButtonDownload, {
    archive: {
      file,
      httpUrl: dataRange ? {
        url: dataRange.url
      } : undefined
    },
    name: name
  }))), /*#__PURE__*/React.createElement(ViewerDocumentContainer, _extends({
    className: clsx('view-pdf-document', {
      'viewer-grab-scrollable': !isMobile && dragMove
    })
  }, dragPan()), loading && /*#__PURE__*/React.createElement(ViewPdfProgress, {
    loading: loading
  }), /*#__PURE__*/React.createElement(Document, {
    file: fileState,
    rotate: rotation,
    onLoadProgress: data => {
      if (data.loaded >= data.total) setLoading(null);else setLoading(data);
    },
    loading: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ViewArchiveInfoLoadingIcon, {
      name: name
    }, password.requesting && /*#__PURE__*/React.createElement(PdfPasswordUnlock, {
      onUnlock: unlock
    }))),
    noData: /*#__PURE__*/React.createElement(ViewArchiveInfoError, null, "No PDF file specified"),
    error: /*#__PURE__*/React.createElement(ViewArchiveInfoError, null, "Error loading pdf document view") // TODO improve message
    ,
    onLoadSuccess: onDocumentLoadSuccess,
    onLoadError: error => {
      console.error('[PDF] Document load error: ', error);
    },
    onPassword: onPassword,
    onItemClick: ({
      pageNumber
    }) => {
      goToPage(pageNumber - 1);
    },
    options: cMapOptions
    // options={{ ...cMapOptions, ...options }}
  }, scale ? /*#__PURE__*/React.createElement(React.Fragment, null, Array.from(new Array(numPages), (el, index) => {
    var _pageIndexAnnotations;
    return /*#__PURE__*/React.createElement(ViewPdfPage, {
      rotate: rotation,
      currentPageIndex: pageIndex,
      pageIndex: index,
      scale: scale,
      dims: pageIndexDims[index],
      key: `page_${index + 1}`,
      ref: node => {
        if (node) pageRefs.current[index] = node;
      },
      onView: setPageIndex,
      onRenderSuccess: () => {
        if (numPages !== 0 && readed.current.length !== numPages) {
          readed.current = Array(numPages).fill(false);
        }
        if (readed.current.length) {
          readed.current[index] = true;
          if (onViewed && readed.current.every(readed => readed)) onViewed();
          // TODO maybe last page must have been scrolled to the bottom to be readed ??
        }
      }
    }, (_pageIndexAnnotations = pageIndexAnnotations[index]) === null || _pageIndexAnnotations === void 0 ? void 0 : _pageIndexAnnotations.annotations.map((Annotation, key) => /*#__PURE__*/React.createElement(Annotation, {
      key: key,
      pageIndex: index,
      numPages: numPages,
      pageDim: pageIndexDims[index],
      scale: scale,
      factor: factor,
      rotation: rotation
    })));
  })) : /*#__PURE__*/React.createElement("div", {
    className: "react-pdf__message react-pdf__message--loading"
  }, /*#__PURE__*/React.createElement(ViewArchiveInfoLoadingIcon, {
    name: name
  })))))));
};

// TODO test Outline component